import React, { useEffect, useState } from 'react';
import { referralsApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Copy, Check, Share2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    referralsApi.me()
      .then(setData)
      .catch((e) => { logger.error('referrals me failed', e); setError(e.message || 'Could not load referrals.'); });
  }, []);

  if (error) return null;

  if (!data) {
    return <Skeleton className="h-32 w-full" data-testid="referral-card-loading" />;
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = `${origin}/?ref=${data.referral_code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied to clipboard.');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.debug('clipboard write failed', err);
      toast.error('Could not copy. Long-press to copy manually.');
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Apex Athletic Mind',
          text: 'Try the AI mental performance coach — my invite link:',
          url: link,
        });
      } catch (err) {
        // User likely cancelled the share sheet — not an error worth surfacing.
        logger.debug('native share dismissed', err);
      }
    } else {
      copy();
    }
  };

  return (
    <Card className="border-border" data-testid="referral-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-[#004C54]" />
          <h3 className="font-heading text-base font-bold">Invite a teammate</h3>
        </div>
        <p className="text-sm text-foreground/65 mb-4">
          Share your link. When a friend signs up and subscribes, you'll earn a free month (Coach Lewis will credit your account).
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 px-3 py-2 rounded-md bg-[#F6F7F8] border border-border text-[13px] font-mono truncate" data-testid="referral-link">
            {link}
          </div>
          <Button onClick={copy} variant="outline" className="shrink-0" data-testid="referral-copy">
            {copied ? <><Check className="h-4 w-4 mr-1.5 text-[#004C54]" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
          </Button>
          <Button onClick={share} className="bg-[#004C54] hover:bg-[#003a40] text-white shrink-0" data-testid="referral-share">
            <Share2 className="h-4 w-4 mr-1.5" /> Share
          </Button>
        </div>
        <div className="flex items-center gap-4 mt-4 text-[12px] text-foreground/60">
          <Badge variant="outline" className="border-foreground/15" data-testid="referral-count-signups">{data.signed_up_count} signups</Badge>
          <Badge variant="outline" className="border-[#004C54]/30 text-[#004C54]" data-testid="referral-count-converted">{data.converted_count} converted</Badge>
        </div>
      </CardContent>
    </Card>
  );
}
